import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { people, storiesByPerson, fmtDate } from "../../data/impactStories";
import { Quote, ArrowUpRight } from "lucide-react";

export default function RiPeople({ onOpenStory }) {
  const [profile, setProfile] = useState(null);
  const recent = profile ? storiesByPerson(profile.name).slice(0, 3) : [];

  return (
    <section id="people" data-testid="ri-people" className="py-24 lg:py-32 bg-[#0c0c0f] scroll-mt-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14 reveal">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">Meet the People</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02]">
            Responsibility has <span className="text-amber-accent">names.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {people.map((p, i) => (
            <button
              key={p.id}
              data-testid={`person-card-${p.id}`}
              onClick={() => setProfile(p)}
              className="reveal group relative text-left rounded-2xl overflow-hidden border border-white/8 bg-[#101014] hover:border-[#F5D000]/50 transition-all"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Portrait placeholder, swaps to real photo when p.image is set */}
              <div className="relative h-72 flex items-center justify-center bg-gradient-to-b from-[#17171c] to-[#0e0e12] overflow-hidden">
                {p.image ? (
                  <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover object-[center_22%] group-hover:scale-[1.04] transition-transform duration-700" />
                ) : (
                  <span className="font-heavy text-7xl text-[#F5D000]/90 group-hover:scale-110 transition-transform duration-500">{p.initials}</span>
                )}
                {/* hover quote */}
                <div className="absolute inset-0 bg-black/85 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex items-center p-6">
                  <div>
                    <Quote className="w-5 h-5 text-[#F5D000] mb-3" />
                    <p className="text-sm text-white/85 italic leading-relaxed">“{p.quote}”</p>
                  </div>
                </div>
              </div>
              <div className="p-5">
                <div className="font-display text-lg font-bold text-white">{p.name}</div>
                <div className="text-xs text-white/55 mt-1 leading-snug">{p.role}</div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#F5D000]">
                  View profile <ArrowUpRight className="w-3 h-3" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Profile modal */}
      <Dialog open={!!profile} onOpenChange={(o) => !o && setProfile(null)}>
        <DialogContent data-testid="person-modal" className="max-w-xl w-[94vw] max-h-[86vh] overflow-y-auto bg-[#0e0e12] border-white/10 p-7 sm:p-9">
          <DialogTitle className="sr-only">{profile ? profile.name : "Profile"}</DialogTitle>
          {profile && (
            <div>
              <div className="flex items-center gap-5 mb-6">
                <div className="w-20 h-20 rounded-2xl bg-[#F5D000] flex items-center justify-center font-heavy text-2xl text-black shrink-0">
                  {profile.initials}
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-white">{profile.name}</div>
                  <div className="text-sm text-[#F5D000] mt-1">{profile.role}</div>
                  <div className="text-xs text-white/50 mt-1">{profile.years} years with R&amp;C</div>
                </div>
              </div>
              <blockquote className="border-l-2 border-[#F5D000] pl-4 text-white/85 italic mb-6">“{profile.quote}”</blockquote>
              <div className="mb-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-2">Responsibilities</div>
                <div className="flex flex-wrap gap-2">
                  {profile.responsibilities.map((r) => (
                    <span key={r} className="px-3 py-1.5 rounded-full border border-white/12 bg-white/5 text-xs text-white/75">{r}</span>
                  ))}
                </div>
              </div>
              <div className="mb-6 text-sm">
                <span className="text-white/45">Favourite project: </span>
                <span className="text-white font-semibold">{profile.favouriteProject}</span>
              </div>
              {recent.length > 0 && (
                <div className="pt-5 border-t border-white/8">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-3">Recent Impact Stories</div>
                  <div className="space-y-2">
                    {recent.map((s) => (
                      <button key={s.id} data-testid={`person-story-${s.id}`}
                        onClick={() => { setProfile(null); onOpenStory(s); }}
                        className="group w-full text-left rounded-lg border border-white/8 bg-white/[0.03] hover:border-[#F5D000]/50 transition-all px-4 py-3 flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-white">{s.title}</div>
                          <div className="text-[11px] text-white/45 mt-0.5">{fmtDate(s.date)}</div>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[#F5D000] shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
